import { useCallback, useEffect, useState } from "react";
import { HiPlus, HiX, HiCheck, HiPencil, HiTrash, HiCube, HiCurrencyEuro, HiArchive } from "react-icons/hi";

export default function StockList() {

    type ContentType = {
        id: Number;
        name: string;
        price: string;
        stock: string;
        isInfinite: boolean;
    }

    const [stockList, setStockList] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [error, setError] = useState('');
    const [edit, setEdit] = useState(false);
    const [content, setContent] = useState<ContentType>({
        id: 0,
        name: '',
        price: '',
        stock: '',
        isInfinite: false
    });

    const fetchDatas = async () => {
        await fetch(`${process.env.REACT_APP_BACK_URL}/stock`)
            .then(response => response.json())
            .then(data => setStockList(data))
            .catch(error => console.error(error));
    }

    const sendDatas = useCallback(() => {
        console.log(content);
        fetch(`${process.env.REACT_APP_BACK_URL}/stock`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(content)
        })
            .then(response => {
                fetchDatas();
                setOpenModal(false);
                setContent({ id: 0, name: '', price: '', stock: '', isInfinite: false });
            })
            .catch(error => console.error(error));
    }, [content]);

    useEffect(() => {
        fetchDatas()
    }, []);

    useEffect(() => {
        if (content.price !== '') {
            const regex = /^[0-9.]+$/;
            if (regex.test(content.price)) {
                setError('')
            } else {
                console.log("non")
                setError("Merci de respecter le format ex: 39.99")
            }
        }
    }, [content]);

    const deleteProduct = async (id: Number) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
        
        await fetch(`${process.env.REACT_APP_BACK_URL}/stock`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: id
            })
        })
            .then(response => {
                fetchDatas();
            })
            .catch(error => console.error(error));
    }

    const openEdit = (p: any) => {
        setContent({
            id: Number(p.id),
            name: p.name ?? '',
            price: String(p.price ?? ''),
            stock: String(p.stock ?? ''),
            isInfinite: p.isInfinite ?? false,
        });
        setEdit(true);
    };

    const sendDatasEdit = useCallback(() => {
        fetch(`${process.env.REACT_APP_BACK_URL}/stock`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(content)
        })
            .then(response => {
                fetchDatas();
                setEdit(false);
                setContent({ id: 0, name: '', price: '', stock: '', isInfinite: false });
            })
            .catch(error => console.error(error));
    }, [content]);

    // Calculate totals
    const totalProducts = stockList.length;
    const lowStockCount = stockList.filter((item: any) => {
        if (item.isInfinite) return false;
        const stockValue = parseInt(String(item.stock), 10);
        return !isNaN(stockValue) && stockValue < 10;
    }).length;
    const infiniteCount = stockList.filter((item: any) => item.isInfinite).length;

    return (
        <>
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h1 className="page-title">Gestion des Produits</h1>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg shadow-indigo-200">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-white/20 rounded-xl">
                            <HiCube className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-indigo-100 text-sm font-medium">Total Produits</p>
                            <p className="text-2xl font-bold">{totalProducts}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white shadow-lg shadow-amber-200">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-white/20 rounded-xl">
                            <HiCurrencyEuro className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-amber-100 text-sm font-medium">Stock Faible</p>
                            <p className="text-2xl font-bold">{lowStockCount} produits</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Products Grid */}
            <div className="card">
                <div className="p-6 border-b border-slate-200">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-slate-800">Inventaire</h2>
                        <button 
                            onClick={() => setOpenModal(true)} 
                            className="btn-primary flex items-center gap-2"
                        >
                            <HiPlus className="w-5 h-5" />
                            Ajouter un produit
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                    {stockList && stockList.map((stock: any, index) => (
                        <div 
                            key={index} 
                            className="bg-slate-50 rounded-2xl p-5 hover:shadow-lg transition-all duration-300 card-hover border border-slate-100 group"
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white shadow-md">
                                    <HiCube className="w-6 h-6" />
                                </div>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button 
                                        onClick={() => openEdit(stock)} 
                                        className="p-2 rounded-lg bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition-colors"
                                    >
                                        <HiPencil className="w-4 h-4" />
                                    </button>
                                    <button 
                                        onClick={() => deleteProduct(stock.id)} 
                                        className="p-2 rounded-lg bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors"
                                    >
                                        <HiTrash className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            
                            <h3 className="font-semibold text-slate-800 text-lg mb-2">{stock.name}</h3>
                            
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-sm text-slate-500">Prix unitaire</p>
                                    <p className="text-xl font-bold text-indigo-600">{stock.price} €</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-slate-500">En stock</p>
                                    {stock.isInfinite ? (
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-700">
                                            ∞ Illimité
                                        </span>
                                    ) : (
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                                            Number(stock.stock) < 10 
                                                ? 'bg-rose-100 text-rose-700' 
                                                : Number(stock.stock) < 30 
                                                    ? 'bg-amber-100 text-amber-700'
                                                    : 'bg-emerald-100 text-emerald-700'
                                        }`}>
                                            {stock.stock}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Add Modal */}
            {openModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                        onClick={() => setOpenModal(false)}
                    ></div>
                    
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scaleIn">
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex items-center justify-between rounded-t-2xl">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                                    <HiPlus className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-white">Nouveau produit</h3>
                            </div>
                            <button 
                                className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                                onClick={() => setOpenModal(false)}
                            >
                                <HiX className="w-5 h-5 text-white" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block mb-2 text-sm font-semibold text-slate-700">Nom du produit</label>
                                <input 
                                    type="text" 
                                    className="input-modern" 
                                    placeholder="Ex: Bière blonde" 
                                    value={content.name}
                                    onChange={(e) => setContent({ ...content, name: e.target.value })} 
                                />
                            </div>
                            <div>
                                <label className="block mb-2 text-sm font-semibold text-slate-700">Prix (€)</label>
                                <input 
                                    type="text" 
                                    className={`input-modern ${error ? 'border-rose-500 focus:border-rose-500' : ''}`}
                                    placeholder="12.99" 
                                    value={content.price}
                                    onChange={(e) => setContent({ ...content, price: e.target.value })} 
                                />
                                {error && <p className="text-rose-500 text-sm mt-1">{error}</p>}
                            </div>
                            <div>
                                <label className="block mb-2 text-sm font-semibold text-slate-700">Quantité en stock</label>
                                <input 
                                    type="number" 
                                    className="input-modern" 
                                    placeholder="50" 
                                    value={content.stock}
                                    onChange={(e) => setContent({ ...content, stock: e.target.value })} 
                                    disabled={content.isInfinite}
                                />
                            </div>
                            <div className="bg-slate-50 rounded-xl p-4">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={content.isInfinite}
                                        onChange={(e) => setContent({ ...content, isInfinite: e.target.checked, stock: e.target.checked ? '0' : content.stock })}
                                        className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                                    />
                                    <div>
                                        <span className="font-semibold text-slate-700">Stock illimité</span>
                                        <p className="text-sm text-slate-500">Le produit est toujours disponible (ex: café)</p>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 rounded-b-2xl border-t border-slate-200">
                            <button 
                                className="btn-secondary"
                                onClick={() => setOpenModal(false)}
                            >
                                Annuler
                            </button>
                            <button 
                                className="btn-primary" 
                                onClick={sendDatas} 
                                disabled={error !== '' || content.name === '' || (!content.isInfinite && content.stock === '') || content.price === ''}
                            >
                                <HiCheck className="w-4 h-4 mr-2 inline" />
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {edit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                        onClick={() => setEdit(false)}
                    ></div>
                    
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scaleIn">
                        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5 flex items-center justify-between rounded-t-2xl">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                                    <HiPencil className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-white">Modifier le produit</h3>
                            </div>
                            <button 
                                className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                                onClick={() => setEdit(false)}
                            >
                                <HiX className="w-5 h-5 text-white" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block mb-2 text-sm font-semibold text-slate-700">Nom du produit</label>
                                <input 
                                    type="text" 
                                    className="input-modern" 
                                    placeholder="Nom" 
                                    value={content.name}
                                    onChange={(e) => setContent({ ...content, name: e.target.value })} 
                                />
                            </div>
                            <div>
                                <label className="block mb-2 text-sm font-semibold text-slate-700">Prix (€)</label>
                                <input 
                                    type="text" 
                                    className={`input-modern ${error ? 'border-rose-500 focus:border-rose-500' : ''}`}
                                    placeholder="12.99" 
                                    value={content.price}
                                    onChange={(e) => setContent({ ...content, price: e.target.value })} 
                                />
                                {error && <p className="text-rose-500 text-sm mt-1">{error}</p>}
                            </div>
                            <div>
                                <label className="block mb-2 text-sm font-semibold text-slate-700">Quantité en stock</label>
                                <input 
                                    type="number" 
                                    className="input-modern" 
                                    placeholder="50" 
                                    value={content.stock}
                                    onChange={(e) => setContent({ ...content, stock: e.target.value })} 
                                    disabled={content.isInfinite}
                                />
                            </div>
                            <div className="bg-slate-50 rounded-xl p-4">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={content.isInfinite}
                                        onChange={(e) => setContent({ ...content, isInfinite: e.target.checked, stock: e.target.checked ? '0' : content.stock })}
                                        className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                                    />
                                    <div>
                                        <span className="font-semibold text-slate-700">Stock illimité</span>
                                        <p className="text-sm text-slate-500">Le produit est toujours disponible (ex: café)</p>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 rounded-b-2xl border-t border-slate-200">
                            <button 
                                className="btn-secondary"
                                onClick={() => setEdit(false)}
                            >
                                Annuler
                            </button>
                            <button 
                                className="btn-primary" 
                                onClick={sendDatasEdit}
                            >
                                <HiCheck className="w-4 h-4 mr-2 inline" />
                                Mettre à jour
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
